/**
 * Category service for managing prompt categories, backed by Firestore.
 * Categories are stored in the `categories` collection; a doc is either
 * global (isGlobal: true, orgId: null) or scoped to an org.
 */

import { collection, doc, getDocs, setDoc, query, where } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../../../shared/firebase';

/**
 * Get all categories
 * @param {Object} filters - Filter options (orgId, isGlobal)
 * @returns {Promise<Array<string>>} Array of category titles
 */
export const getAllCategories = async (filters = {}) => {
  try {
    let q = collection(db, 'categories');
    const clauses = [];
    if (filters.isGlobal !== undefined) {
      clauses.push(where('isGlobal', '==', !!filters.isGlobal));
    }
    if (filters.orgId !== undefined) {
      clauses.push(where('orgId', '==', filters.orgId));
    }
    if (clauses.length) {
      q = query(q, ...clauses);
    }
    const snap = await getDocs(q);
    const titles = [];
    snap.forEach((d) => titles.push(d.data().categoryTitle));
    return titles.sort((a, b) => a.localeCompare(b));
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Add a new category
 * @param {string} categoryName - The category name
 * @param {Object} options - Options (isGlobal, orgId)
 * @returns {Promise<string>} The created category title
 */
export const addCategory = async (categoryName, options = {}) => {
  try {
    const categoryId = uuidv4();
    await setDoc(doc(db, 'categories', categoryId), {
      categoryId,
      categoryTitle: categoryName,
      isGlobal: options.isGlobal || false,
      orgId: options.orgId || null,
      createdAt: new Date().toISOString(),
    });
    return categoryName;
  } catch (error) {
    console.error('Error adding category:', error);
    throw error;
  }
};

/**
 * Check if category exists
 * @param {string} categoryName - The category name
 * @param {Object} filters - Filter options (orgId, isGlobal)
 */
export const categoryExists = async (categoryName, filters = {}) => {
  try {
    const categories = await getAllCategories(filters);
    return categories.includes(categoryName);
  } catch (error) {
    console.error('Error checking category existence:', error);
    return false;
  }
};
